import { z } from 'zod';

import { ActorLoginSchema, GithubLoginSchema } from './github-login';

// ---------------------------------------------------------------------------
// The activity feed: a normalised stream of engineering events per org, with a
// per-viewer inbox ("Mine") layered over an org firehose ("All").
//
// Kinds, reasons, coverage and the response DTOs. The per-kind `detail`
// payloads live in `activity-detail.ts`, which imports from here — one way
// only, so there is no cycle between the enum layer and its validators.
//
// Dates crossing this boundary are ISO strings (the repo contract rule);
// Convex stores epoch-ms internally and the core does the mapping.
// ---------------------------------------------------------------------------

/** Every event kind the feed can carry. */
export const ActivityKindSchema = z.enum([
  // Commits and refs
  'commits_pushed',
  'branch_created',
  'branch_deleted',
  'tag_created',
  'tag_deleted',
  'commit_commented',
  // Pull request lifecycle beyond closing
  'pr_opened',
  'pr_reopened',
  'pr_ready_for_review',
  'pr_converted_to_draft',
  'pr_metadata_changed',
  // GitHub issues
  'issue_opened',
  'issue_reopened',
  'issue_closed',
  'issue_commented',
  'issue_metadata_changed',
  // Review activity
  'review_approved',
  'review_changes_requested',
  'review_commented',
  'review_comment_replied',
  'review_dismissed',
  'pr_comment',
  // Asks
  'review_requested',
  'review_requested_team',
  'assigned',
  // Lifecycle
  'pr_merged',
  'pr_closed_unmerged',
  // Checks
  'check_run_failed',
  // Kruxit-native
  'scan_delta',
  'scan_finding',
  'scan_resolved',
  'next_up_changed',
]);
export type ActivityKind = z.infer<typeof ActivityKindSchema>;

/** Coarse grouping, for filter chips and MCP summaries. */
export const ActivityCategorySchema = z.enum([
  'commit',
  'issue',
  'metadata',
  'review',
  'lifecycle',
  'checks',
  'insight',
]);
export type ActivityCategory = z.infer<typeof ActivityCategorySchema>;

/** Which ingest path produced a row. Surfaced so a gap is diagnosable. */
export const IngestPathSchema = z.enum(['webhook', 'poll', 'derived']);
export type IngestPath = z.infer<typeof IngestPathSchema>;

/**
 * Why an event is in your inbox.
 *
 * Deliberately omitted in v1: `team_reviewer` (GitHub team membership is not
 * mirrored anywhere, so a team review request cannot be expanded to logins) and
 * `owner` (no CODEOWNERS ingestion). Both are reserved names — add them only
 * alongside the data that makes them true.
 */
export const ActivityReasonSchema = z.enum([
  'author',
  'reviewer',
  'assignee',
  'mentioned',
  'participant',
  'self',
  'admin',
]);
export type ActivityReason = z.infer<typeof ActivityReasonSchema>;

/**
 * Kind → category. Declared as a full record so a new kind that forgets a
 * category is a compile error, then read through a Map: indexing a record with
 * a variable key trips `security/detect-object-injection`, and the rule is
 * right in general even though the key here is a validated enum member.
 */
const CATEGORY_BY_KIND: Readonly<Record<ActivityKind, ActivityCategory>> = {
  commits_pushed: 'commit',
  branch_created: 'metadata',
  branch_deleted: 'metadata',
  tag_created: 'metadata',
  tag_deleted: 'metadata',
  commit_commented: 'commit',
  pr_opened: 'lifecycle',
  pr_reopened: 'lifecycle',
  pr_ready_for_review: 'lifecycle',
  pr_converted_to_draft: 'lifecycle',
  pr_metadata_changed: 'metadata',
  issue_opened: 'issue',
  issue_reopened: 'issue',
  issue_closed: 'issue',
  issue_commented: 'issue',
  issue_metadata_changed: 'metadata',
  review_approved: 'review',
  review_changes_requested: 'review',
  review_commented: 'review',
  review_comment_replied: 'review',
  review_dismissed: 'review',
  pr_comment: 'review',
  review_requested: 'review',
  review_requested_team: 'review',
  assigned: 'lifecycle',
  pr_merged: 'lifecycle',
  pr_closed_unmerged: 'lifecycle',
  check_run_failed: 'checks',
  scan_delta: 'insight',
  scan_finding: 'insight',
  scan_resolved: 'insight',
  next_up_changed: 'insight',
};

const CATEGORY_LOOKUP = new Map<ActivityKind, ActivityCategory>(
  Object.entries(CATEGORY_BY_KIND) as readonly [
    ActivityKind,
    ActivityCategory,
  ][],
);

/** The category a kind belongs to. Total by construction. */
export function categoryForKind(kind: ActivityKind): ActivityCategory {
  // Non-null: `CATEGORY_BY_KIND` is a total record over `ActivityKind`, and the
  // parameter is that union, so every key is present.
  return CATEGORY_LOOKUP.get(kind) ?? 'insight';
}

// --- coverage --------------------------------------------------------------

/**
 * How complete "All" actually is.
 *
 * This is not decoration. The PAT poller uses viewer-shaped queries (`@me`,
 * "review requested for me"), so a `pat` org's firehose is really "everything
 * the connected users can see" — a subset that looks identical to a complete
 * one. Both the UI banner and the MCP header render this so neither a person
 * nor an agent can silently reason over a partial feed.
 */
export const ActivityCoverageSchema = z.enum(['app', 'pat', 'mixed', 'none']);
export type ActivityCoverage = z.infer<typeof ActivityCoverageSchema>;

export const ActivityCoverageReasonSchema = z.enum([
  'app-installed',
  'app-partial-repos',
  'no-github-app-installed',
  'no-source-connected',
]);
export type ActivityCoverageReason = z.infer<
  typeof ActivityCoverageReasonSchema
>;

// --- feed DTOs -------------------------------------------------------------

export const ActivityEventSchema = z.object({
  id: z.string(),
  kind: ActivityKindSchema,
  category: ActivityCategorySchema,
  /** The source's instant, ISO — not when we ingested it. */
  occurredAt: z.iso.datetime(),
  ingestPath: IngestPathSchema,
  repo: z.string(),
  subjectKey: z.string(),
  actorLogin: ActorLoginSchema.nullable(),
  actorIsBot: z.boolean(),
  title: z.string(),
  url: z.url(),
  /** `null` when the stored payload could not be parsed — render the title. */
  detail: z.unknown().nullable(),
  /** Present only on a "Mine" row. */
  reason: ActivityReasonSchema.optional(),
  read: z.boolean().optional(),
});
// `Readonly<…>` rather than the bare inference: these DTOs are threaded through
// every mapping and formatting function, and a mutable one both misstates the
// intent (nothing may edit a feed row after it is shaped) and makes every
// consumer noisy under `prefer-readonly-parameter-types`.
export type ActivityEvent = Readonly<z.infer<typeof ActivityEventSchema>>;

/**
 * A standing condition, not an event: a PR that has gone quiet.
 *
 * Derived at read time from the PR mirror rather than materialised, so a PR
 * that gets reviewed simply stops appearing. A stored staleness event would
 * have to be explicitly superseded or it would keep asserting something that
 * stopped being true.
 */
export const AgeingItemSchema = z.object({
  subjectKey: z.string(),
  repo: z.string(),
  title: z.string(),
  url: z.url(),
  author: GithubLoginSchema.nullable(),
  updatedAt: z.iso.datetime(),
  /** Working hours idle — weekends excluded, per the staleness rule. */
  idleWorkingHours: z.number().nonnegative(),
  /** True when the viewer is author, assignee or a requested reviewer. */
  isMine: z.boolean(),
});
export type AgeingItem = Readonly<z.infer<typeof AgeingItemSchema>>;

/**
 * How long a PR must be idle before it counts as quiet, and how long before it
 * stops being an attention item at all.
 *
 * A "day" is 24 WORKING hours (weekends excluded), matching the measure these
 * are compared against — NOT an 8-hour workday. `staleAfterDays: 1` is exactly
 * the 24-working-hour rule this replaced.
 */
export const STALE_AFTER_DAYS_DEFAULT = 1;
export const ABANDONED_AFTER_DAYS_DEFAULT = 30;

export const MIN_STALE_AFTER_DAYS = 1;
export const MAX_STALE_AFTER_DAYS = 14;
export const MIN_ABANDONED_AFTER_DAYS = 7;
export const MAX_ABANDONED_AFTER_DAYS = 365;

/** Working hours in one threshold "day". */
export const WORKING_HOURS_PER_DAY = 24;

export const AgeingSettingSourceSchema = z.enum(['global', 'org', 'user']);
export type AgeingSettingSource = z.infer<typeof AgeingSettingSourceSchema>;

/**
 * The thresholds in force for a viewer, and where each came from.
 *
 * `source` is carried rather than inferred so a reader can tell an inherited
 * default from a deliberate override without a second round trip — and so an
 * agent can say WHY old pull requests are missing instead of guessing.
 */
export const AgeingSettingsSchema = z.object({
  staleAfterDays: z
    .number()
    .int()
    .min(MIN_STALE_AFTER_DAYS)
    .max(MAX_STALE_AFTER_DAYS),
  abandonedAfterDays: z
    .number()
    .int()
    .min(MIN_ABANDONED_AFTER_DAYS)
    .max(MAX_ABANDONED_AFTER_DAYS),
  source: z.object({
    staleAfterDays: AgeingSettingSourceSchema,
    abandonedAfterDays: AgeingSettingSourceSchema,
  }),
});
export type AgeingSettings = Readonly<z.infer<typeof AgeingSettingsSchema>>;

export const ActivityFeedResponseSchema = z.object({
  events: z.array(ActivityEventSchema),
  /** Quiet pull requests only — abandoned ones are counted, not listed. */
  ageing: z.array(AgeingItemSchema),
  /**
   * Quiet for longer than `abandonedAfterDays`, and therefore omitted above.
   *
   * Reported rather than silently dropped: "no pull requests have gone quiet"
   * and "33 went quiet months ago and are hidden" are very different states,
   * and a reader cannot tell them apart from an empty list.
   */
  abandonedCount: z.number().int().nonnegative(),
  /**
   * True when `abandonedCount` hit its cap and is therefore a LOWER BOUND.
   *
   * Carried rather than inferred: a capped 200 rendered as "200 hidden" is a
   * confident number that happens to be wrong, and the whole point of
   * reporting the count was to stop a filtered list reading as a complete one.
   */
  abandonedAtLeast: z.boolean(),
  ageingSettings: AgeingSettingsSchema,
  coverage: ActivityCoverageSchema,
  coverageReason: ActivityCoverageReasonSchema,
  unreadCount: z.number().int().nonnegative(),
  /** Opaque Convex pagination cursor; `null` when the last page was reached. */
  cursor: z.string().nullable(),
});
export type ActivityFeedResponse = z.infer<typeof ActivityFeedResponseSchema>;

/** Every non-success payload returned by the Activity HTTP endpoint. */
export const ActivityRouteErrorResponseSchema = z.object({
  error: z.enum([
    'unauthenticated',
    'payment_required',
    'invalid_request',
    'unconfigured',
    'unavailable',
  ]),
});
export type ActivityRouteError = z.infer<
  typeof ActivityRouteErrorResponseSchema
>['error'];

/**
 * The person-first timeline shown by the Activity page.
 *
 * This is deliberately smaller than {@link ActivityFeedResponse}: a timeline
 * is a history of actions, not the viewer's inbox, so receipt/read state and
 * ageing conditions do not cross this boundary.
 */
export const ActivityTimelineResponseSchema = z.object({
  events: z.array(ActivityEventSchema.omit({ reason: true, read: true })),
  coverage: ActivityCoverageSchema,
  coverageReason: ActivityCoverageReasonSchema,
  /** Opaque Convex pagination cursor; `null` on the last page. */
  cursor: z.string().nullable(),
});
export type ActivityTimelineResponse = z.infer<
  typeof ActivityTimelineResponseSchema
>;

/** One selected person, the signed-in person, or the whole workspace. */
export const ActivityTimelinePersonSchema = z.union([
  z.literal('@me'),
  z.literal('all'),
  GithubLoginSchema,
]);
export type ActivityTimelinePerson = z.infer<
  typeof ActivityTimelinePersonSchema
>;

/** Response from marking one bounded batch of activity receipts read. */
export const ActivityMarkReadResponseSchema = z.object({
  updated: z.number().int().nonnegative(),
});
export type ActivityMarkReadResponse = z.infer<
  typeof ActivityMarkReadResponseSchema
>;

export const ActivityScopeSchema = z.enum(['mine', 'all']);
export type ActivityScope = z.infer<typeof ActivityScopeSchema>;
